import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  UploadFile as UploadIcon,
  AccountBalance as BankIcon,
  CheckCircle as DoneIcon,
  Block as IgnoreIcon,
  DirectionsCar as CarIcon,
  SwapHoriz as MoveIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CATEGORIAS_ENTRADA_CAIXA,
  CATEGORIAS_SAIDA_CAIXA,
  EXPENSE_CATEGORIES,
  ExpenseCategory,
  Vehicle,
  CategoriaEntradaCaixa,
  CategoriaSaidaCaixa,
} from '../../types';
import { OfxStatement, OfxTransaction, readOfxFile } from '../../utils/ofxParser';
import vehicleService from '../../services/vehicleService';
import expenseService from '../../services/expenseService';
import { cashMovementService } from '../../services/caixaService';

type ClassificationKind = 'pending' | 'ignore' | 'expense' | 'movement' | 'registered';

interface ExpenseDraft {
  vehicle_id: number;
  category: ExpenseCategory;
  description: string;
  amount: number;
  expense_date: string;
  deduct_from_store_cash: boolean;
  notes?: string;
}

interface MovementDraft {
  type: 'entrada' | 'saida';
  category: string;
  amount: number;
  description: string;
  date: string;
}

interface RowState {
  classification: ClassificationKind;
  expense?: ExpenseDraft;
  movement?: MovementDraft;
  error?: string;
}

const formatCurrency = (value: number): string =>
  Math.abs(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (iso: string): string => {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return iso;
  }
};

const vehicleLabel = (v: Vehicle): string => {
  const plate = v.plate ? ` · ${v.plate}` : '';
  return `${v.brand || ''} ${v.model || ''}${plate}`.trim() || `Veículo #${v.id}`;
};

const kpiSx = {
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
};

const ConciliacaoBancaria: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [statement, setStatement] = useState<OfxStatement | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'classified' | 'registered'>('all');
  const [dialogTx, setDialogTx] = useState<OfxTransaction | null>(null);
  const [dialogKind, setDialogKind] = useState<'expense' | 'movement' | 'ignore'>('movement');
  const [expenseDraft, setExpenseDraft] = useState<ExpenseDraft | null>(null);
  const [movementDraft, setMovementDraft] = useState<MovementDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingVehicles(true);
        const list = await vehicleService.getAllVehicles();
        setVehicles(list || []);
      } catch {
        setSnackbar('Não foi possível carregar os veículos.');
      } finally {
        setLoadingVehicles(false);
      }
    };
    void load();
  }, []);

  const initRows = (txs: OfxTransaction[]) => {
    const next: Record<string, RowState> = {};
    txs.forEach((t) => {
      next[t.id] = { classification: 'pending' };
    });
    setRows(next);
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setParseError(null);
    try {
      const parsed = await readOfxFile(file);
      if (!parsed.transactions.length) {
        setParseError('Nenhum lançamento encontrado neste OFX.');
        setStatement(null);
        return;
      }
      setStatement(parsed);
      setFileName(file.name);
      initRows(parsed.transactions);
      setFilter('all');
    } catch (e) {
      setStatement(null);
      setParseError(e instanceof Error ? e.message : 'Falha ao ler o OFX.');
    }
  };

  const openClassify = (tx: OfxTransaction, preferred?: 'expense' | 'movement' | 'ignore') => {
    const existing = rows[tx.id];
    const abs = Math.abs(tx.amount);
    const kind =
      preferred ||
      (existing?.classification === 'expense' || existing?.classification === 'movement' || existing?.classification === 'ignore'
        ? existing.classification
        : tx.amount < 0
          ? 'expense'
          : 'movement');

    setDialogKind(kind === 'ignore' ? 'ignore' : kind);
    setExpenseDraft(
      existing?.expense || {
        vehicle_id: 0,
        category: 'other',
        description: tx.description,
        amount: abs,
        expense_date: tx.date,
        deduct_from_store_cash: true,
        notes: `OFX FITID: ${tx.fitId || tx.id}`,
      }
    );
    setMovementDraft(
      existing?.movement || {
        type: tx.amount >= 0 ? 'entrada' : 'saida',
        category:
          tx.amount >= 0 ? CATEGORIAS_ENTRADA_CAIXA[0].value : CATEGORIAS_SAIDA_CAIXA[0].value,
        amount: abs,
        description: tx.description,
        date: tx.date,
      }
    );
    setDialogTx(tx);
  };

  const applyClassification = () => {
    if (!dialogTx) return;
    if (dialogKind === 'ignore') {
      setRows((prev) => ({
        ...prev,
        [dialogTx.id]: { classification: 'ignore' },
      }));
      setDialogTx(null);
      return;
    }
    if (dialogKind === 'expense') {
      if (!expenseDraft?.vehicle_id) {
        setSnackbar('Selecione o veículo da despesa.');
        return;
      }
      if (!expenseDraft.description.trim() || expenseDraft.amount <= 0) {
        setSnackbar('Preencha descrição e valor da despesa.');
        return;
      }
      setRows((prev) => ({
        ...prev,
        [dialogTx.id]: { classification: 'expense', expense: expenseDraft },
      }));
      setDialogTx(null);
      return;
    }
    if (!movementDraft?.description.trim() || !movementDraft.amount || movementDraft.amount <= 0) {
      setSnackbar('Preencha descrição e valor da movimentação.');
      return;
    }
    setRows((prev) => ({
      ...prev,
      [dialogTx.id]: { classification: 'movement', movement: movementDraft },
    }));
    setDialogTx(null);
  };

  const registerOne = async (tx: OfxTransaction): Promise<boolean> => {
    const state = rows[tx.id];
    if (!state || state.classification === 'pending' || state.classification === 'registered') {
      return false;
    }
    if (state.classification === 'ignore') {
      setRows((prev) => ({ ...prev, [tx.id]: { classification: 'registered' } }));
      return true;
    }
    try {
      if (state.classification === 'expense' && state.expense) {
        const fd = new FormData();
        fd.append('vehicle_id', String(state.expense.vehicle_id));
        fd.append('category', state.expense.category);
        fd.append('description', state.expense.description);
        fd.append('amount', String(state.expense.amount));
        fd.append('expense_date', state.expense.expense_date);
        fd.append('type', 'expense');
        fd.append('status', 'paid');
        fd.append('deduct_from_store_cash', state.expense.deduct_from_store_cash ? '1' : '0');
        if (state.expense.notes) fd.append('notes', state.expense.notes);
        await expenseService.createExpense(fd);
      } else if (state.classification === 'movement' && state.movement) {
        await cashMovementService.create({
          type: state.movement.type,
          category: state.movement.category as CategoriaEntradaCaixa | CategoriaSaidaCaixa,
          amount: state.movement.amount,
          description: state.movement.description,
          date: state.movement.date,
          ...(state.movement.type === 'saida' ? { payableStatus: 'paid' as const } : {}),
        });
      }
      setRows((prev) => ({ ...prev, [tx.id]: { ...prev[tx.id], classification: 'registered', error: undefined } }));
      return true;
    } catch {
      setRows((prev) => ({
        ...prev,
        [tx.id]: { ...prev[tx.id], error: 'Falha ao registrar. Tente novamente.' },
      }));
      return false;
    }
  };

  const registerReady = async () => {
    if (!statement) return;
    const ready = statement.transactions.filter((t) => {
      const c = rows[t.id]?.classification;
      return c === 'expense' || c === 'movement' || c === 'ignore';
    });
    if (!ready.length) {
      setSnackbar('Classifique ao menos um lançamento antes de registrar.');
      return;
    }
    setSaving(true);
    let ok = 0;
    for (const tx of ready) {
      if (await registerOne(tx)) ok += 1;
    }
    setSaving(false);
    setSnackbar(`${ok} de ${ready.length} lançamento(s) processado(s).`);
  };

  const stats = useMemo(() => {
    const txs = statement?.transactions || [];
    let credits = 0;
    let debits = 0;
    let pending = 0;
    let classified = 0;
    let registered = 0;
    txs.forEach((t) => {
      if (t.amount >= 0) credits += t.amount;
      else debits += Math.abs(t.amount);
      const c = rows[t.id]?.classification || 'pending';
      if (c === 'pending') pending += 1;
      else if (c === 'registered') registered += 1;
      else classified += 1;
    });
    return { credits, debits, pending, classified, registered, total: txs.length };
  }, [statement, rows]);

  const visibleTxs = useMemo(() => {
    const txs = statement?.transactions || [];
    return txs.filter((t) => {
      const c = rows[t.id]?.classification || 'pending';
      if (filter === 'pending') return c === 'pending';
      if (filter === 'classified') return c === 'expense' || c === 'movement' || c === 'ignore';
      if (filter === 'registered') return c === 'registered';
      return true;
    });
  }, [statement, rows, filter]);

  const classificationChip = (tx: OfxTransaction) => {
    const c = rows[tx.id]?.classification || 'pending';
    if (c === 'pending') return <Chip size="small" label="Pendente" />;
    if (c === 'ignore') return <Chip size="small" color="default" icon={<IgnoreIcon />} label="Ignorar" />;
    if (c === 'expense') return <Chip size="small" color="error" icon={<CarIcon />} label="Despesa veículo" />;
    if (c === 'movement') return <Chip size="small" color="info" icon={<MoveIcon />} label="Movimentação" />;
    return <Chip size="small" color="success" icon={<DoneIcon />} label="Registrado" />;
  };

  const selectedVehicle =
    vehicles.find((v) => v.id === expenseDraft?.vehicle_id) || null;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Conciliação bancária
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Importe um extrato OFX no navegador, classifique cada lançamento e registre como despesa de veículo ou movimentação de caixa.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".ofx,.OFX,application/x-ofx,application/ofx,text/plain"
            hidden
            onChange={(e) => void handleFile(e.target.files?.[0] || null)}
          />
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Importar OFX
          </Button>
          <Button
            variant="outlined"
            disabled={!statement || saving || stats.classified === 0}
            onClick={() => void registerReady()}
            startIcon={saving ? <CircularProgress size={16} /> : <DoneIcon />}
          >
            Registrar classificados
          </Button>
        </Stack>
      </Stack>

      {parseError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {parseError}
        </Alert>
      )}

      {!statement ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <BankIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Nenhum extrato carregado
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Selecione um arquivo <strong>.ofx</strong> exportado do internet banking. A leitura é feita só no seu navegador.
          </Typography>
          <Button variant="contained" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()}>
            Escolher arquivo OFX
          </Button>
        </Paper>
      ) : (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={kpiSx}>
                <Typography variant="caption" color="text.secondary">
                  Arquivo
                </Typography>
                <Typography variant="subtitle1" fontWeight={600} noWrap title={fileName}>
                  {fileName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Conta {statement.accountId || '—'}
                  {statement.startDate && statement.endDate
                    ? ` · ${formatDate(statement.startDate)} a ${formatDate(statement.endDate)}`
                    : ''}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Paper sx={kpiSx}>
                <Typography variant="caption" color="text.secondary">
                  Créditos
                </Typography>
                <Typography variant="h6" color="success.main" fontWeight={700}>
                  {formatCurrency(stats.credits)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Paper sx={kpiSx}>
                <Typography variant="caption" color="text.secondary">
                  Débitos
                </Typography>
                <Typography variant="h6" color="error.main" fontWeight={700}>
                  {formatCurrency(stats.debits)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4} sm={4} md={1.5}>
              <Paper sx={kpiSx}>
                <Typography variant="caption" color="text.secondary">
                  Pendentes
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {stats.pending}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4} sm={4} md={1.5}>
              <Paper sx={kpiSx}>
                <Typography variant="caption" color="text.secondary">
                  Classificados
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {stats.classified}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4} sm={4} md={2}>
              <Paper sx={kpiSx}>
                <Typography variant="caption" color="text.secondary">
                  Registrados
                </Typography>
                <Typography variant="h6" color="success.main" fontWeight={700}>
                  {stats.registered}/{stats.total}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
            {(
              [
                ['all', 'Todos'],
                ['pending', 'Pendentes'],
                ['classified', 'Classificados'],
                ['registered', 'Registrados'],
              ] as const
            ).map(([key, label]) => (
              <Chip
                key={key}
                label={label}
                clickable
                color={filter === key ? 'primary' : 'default'}
                variant={filter === key ? 'filled' : 'outlined'}
                onClick={() => setFilter(key)}
              />
            ))}
          </Stack>

          <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Classificação</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTxs.map((tx) => {
                  const state = rows[tx.id];
                  const isRegistered = state?.classification === 'registered';
                  return (
                    <TableRow key={tx.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(tx.date)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{tx.description}</Typography>
                        {tx.fitId && (
                          <Typography variant="caption" color="text.secondary">
                            FITID {tx.fitId}
                          </Typography>
                        )}
                        {state?.error && (
                          <Typography variant="caption" color="error" display="block">
                            {state.error}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={tx.amount >= 0 ? 'success.main' : 'error.main'}
                        >
                          {tx.amount >= 0 ? '+' : '−'}
                          {formatCurrency(tx.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>{classificationChip(tx)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            disabled={isRegistered}
                            onClick={() => openClassify(tx)}
                          >
                            Classificar
                          </Button>
                          {(state?.classification === 'expense' ||
                            state?.classification === 'movement' ||
                            state?.classification === 'ignore') && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={saving}
                              onClick={() => void registerOne(tx).then((ok) => ok && setSnackbar('Lançamento registrado.'))}
                            >
                              Registrar
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!visibleTxs.length && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        Nenhum lançamento neste filtro.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={!!dialogTx} onClose={() => setDialogTx(null)} fullWidth maxWidth="sm">
        <DialogTitle>Classificar lançamento</DialogTitle>
        <DialogContent dividers>
          {dialogTx && (
            <Stack spacing={2}>
              <Alert severity="info" icon={false}>
                <Typography variant="body2">
                  {formatDate(dialogTx.date)} ·{' '}
                  <strong>
                    {dialogTx.amount >= 0 ? '+' : '−'}
                    {formatCurrency(dialogTx.amount)}
                  </strong>
                </Typography>
                <Typography variant="body2">{dialogTx.description}</Typography>
              </Alert>

              <FormControl>
                <RadioGroup
                  value={dialogKind}
                  onChange={(_, v) => setDialogKind(v as typeof dialogKind)}
                >
                  <FormControlLabel value="expense" control={<Radio />} label="Despesa de veículo" />
                  <FormControlLabel value="movement" control={<Radio />} label="Movimentação de caixa" />
                  <FormControlLabel value="ignore" control={<Radio />} label="Ignorar (não registrar)" />
                </RadioGroup>
              </FormControl>

              {dialogKind === 'expense' && expenseDraft && (
                <Stack spacing={2}>
                  <Autocomplete
                    options={vehicles}
                    loading={loadingVehicles}
                    value={selectedVehicle}
                    getOptionLabel={vehicleLabel}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, v) =>
                      setExpenseDraft((prev) => (prev ? { ...prev, vehicle_id: v?.id || 0 } : prev))
                    }
                    renderInput={(params) => <TextField {...params} label="Veículo" required />}
                  />
                  <FormControl fullWidth>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                      label="Categoria"
                      value={expenseDraft.category}
                      onChange={(e) =>
                        setExpenseDraft((prev) =>
                          prev ? { ...prev, category: e.target.value as ExpenseCategory } : prev
                        )
                      }
                    >
                      {Object.entries(EXPENSE_CATEGORIES).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Descrição"
                    fullWidth
                    value={expenseDraft.description}
                    onChange={(e) =>
                      setExpenseDraft((prev) => (prev ? { ...prev, description: e.target.value } : prev))
                    }
                  />
                  <TextField
                    label="Valor"
                    type="number"
                    fullWidth
                    value={expenseDraft.amount}
                    onChange={(e) =>
                      setExpenseDraft((prev) =>
                        prev ? { ...prev, amount: Number(e.target.value) || 0 } : prev
                      )
                    }
                  />
                  <TextField
                    label="Data"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={expenseDraft.expense_date}
                    onChange={(e) =>
                      setExpenseDraft((prev) =>
                        prev ? { ...prev, expense_date: e.target.value } : prev
                      )
                    }
                  />
                  <FormControl>
                    <Typography variant="caption" color="text.secondary" mb={0.5}>
                      Impacto no caixa
                    </Typography>
                    <RadioGroup
                      row
                      value={expenseDraft.deduct_from_store_cash ? '1' : '0'}
                      onChange={(_, v) =>
                        setExpenseDraft((prev) =>
                          prev ? { ...prev, deduct_from_store_cash: v === '1' } : prev
                        )
                      }
                    >
                      <FormControlLabel value="1" control={<Radio />} label="Debita caixa da loja" />
                      <FormControlLabel value="0" control={<Radio />} label="Só no lucro do veículo" />
                    </RadioGroup>
                  </FormControl>
                </Stack>
              )}

              {dialogKind === 'movement' && movementDraft && (
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      label="Tipo"
                      value={movementDraft.type}
                      onChange={(e) => {
                        const type = e.target.value as 'entrada' | 'saida';
                        setMovementDraft((prev) =>
                          prev
                            ? {
                                ...prev,
                                type,
                                category:
                                  type === 'entrada'
                                    ? CATEGORIAS_ENTRADA_CAIXA[0].value
                                    : CATEGORIAS_SAIDA_CAIXA[0].value,
                              }
                            : prev
                        );
                      }}
                    >
                      <MenuItem value="entrada">Entrada</MenuItem>
                      <MenuItem value="saida">Saída</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                      label="Categoria"
                      value={movementDraft.category}
                      onChange={(e) =>
                        setMovementDraft((prev) =>
                          prev ? { ...prev, category: e.target.value } : prev
                        )
                      }
                    >
                      {(movementDraft.type === 'entrada'
                        ? CATEGORIAS_ENTRADA_CAIXA
                        : CATEGORIAS_SAIDA_CAIXA
                      ).map((cat) => (
                        <MenuItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Descrição"
                    fullWidth
                    value={movementDraft.description}
                    onChange={(e) =>
                      setMovementDraft((prev) =>
                        prev ? { ...prev, description: e.target.value } : prev
                      )
                    }
                  />
                  <TextField
                    label="Valor"
                    type="number"
                    fullWidth
                    value={movementDraft.amount}
                    onChange={(e) =>
                      setMovementDraft((prev) =>
                        prev ? { ...prev, amount: Number(e.target.value) || 0 } : prev
                      )
                    }
                  />
                  <TextField
                    label="Data"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={movementDraft.date}
                    onChange={(e) =>
                      setMovementDraft((prev) => (prev ? { ...prev, date: e.target.value } : prev))
                    }
                  />
                </Stack>
              )}

              {dialogKind === 'ignore' && (
                <Alert severity="warning">
                  Este lançamento não será enviado ao sistema. Use para saldos, transferências internas ou itens já lançados.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogTx(null)}>Cancelar</Button>
          <Button variant="contained" onClick={applyClassification}>
            Salvar classificação
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Box>
  );
};

export default ConciliacaoBancaria;
